import _ from "lodash";
import { connect } from "react-redux";
import { deviceDetect } from "react-device-detect";
import React, { Component } from "react";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";

import { ACPSatisfactionSurvey } from "@/features/assessments/ACPSatisfaction";
import { ActionSection } from "@/components/Navigation/Toolbar/ActionSection/ActionSection";
import Assessment from "@/features/assessments/Assessment";
import AVCheck from "@/components/Common/AVCheck";
import { baseRoute } from "@/config/roles";
import { BrandedHeader } from "@/components/Common/BrandedHeader/BrandedHeader";
import ChangePassword from "@/components/Authentication/ChangePassword";
import { fetchPatientPaywall } from "@/redux/actions/patientCardInfo";
import { Footer } from "@/component-library/Footer";
import GAD7 from "@/features/assessments/GAD7";
import { getAssessmentStatus, updateScheduleList } from "@/redux/actions/patient";
import { getPatientProfile, profileFetchSuccess } from "@/redux/actions/auth";
import { isAdult } from "@/utils/profileUtilsTyped";
import { Main2 } from "@/components/layout/Main";
import mapLabelToComponent from "@/portals/(shared)/helpers/mapLabelToComponent";
import MessagesView from "@/components/Common/MessagesView/MessagesView";
import MobileAddPayment from "@/components/Common/MobileAddPayment/MobileAddPayment";
import { PatientHome } from "@/portals/(patient)/(home)/PatientHome";
import { PatientSettings } from "@/portals/(patient)/(settings)/PatientSettings";
import PHQ9 from "@/features/assessments/PHQ9";
import PrivacyPolicy from "@/components/Common/PrivacyPolicy";
import { selectIsACPEligible } from "@/redux/selectors/patientSelectors";
import { SNSEAssessment } from "@/features/assessments/SNSE";
import Spinner from "@/components/UI/Spinner";
import store from "@/redux/store";
import Survey from "@/components/Patient/NinetyDayCareProgressGuide/SurveyWrapper/Survey";
import SystemCheck from "@/components/Common/SystemCheck";
import TeensAndKidsCareProgram from "@/components/Patient/TeensAndKidsCareProgram";
import { Toolbar } from "@/components/Navigation/Toolbar/Toolbar";
import TutorialScreens from "@/components/Common/TutorialScreen";
import VideoClient from "@/portals/(shared)/(video-call)/VideoCall";

import AppBanner from "./components/AppBanner";
import { LogInTaskProvider } from "./(login-tasks)/LogInTaskContext";
import { LogInTasksRouter } from "./(login-tasks)/LoginTasksRouter";
import { PatientConsents } from "./(consents)/PatientConsents";
import PatientInsurance from "./(insurance)/PatientInsurance";
import PatientNotifications from "./(notifications)/PatientNotifications";
import PatientProfile from "./(profile)/PatientProfile";
import { PaymentPaywall } from "./(login-tasks)/PaymentPaywall";
import PaymentsPage from "./(payments)/PaymentsPage";
import { PolicyAcknowledgement } from "./(login-tasks)/PolicyAcknowledgement";
import PostSessionSurveyWrapper from "./(post-session-survey)/PostSessionSurveyWrapper";
import ScheduleRouter from "./(schedule)/ScheduleRouter";

import "./styles.scss";

class PatientRouter extends Component {
  deviceInfo = deviceDetect();

  ROUTE_SECTION_OPTIONS = [
    {
      activeIconComponent: mapLabelToComponent("Home", true),
      icon: "NavMenu__home-icon",
      iconComponent: mapLabelToComponent("Home"),
      label: "Home",
      mobileIcon: "member_home.svg",
      route: this.props.url + "/home",
    },
    {
      activeIconComponent: mapLabelToComponent("Schedule", true),
      icon: "NavMenu__schedule-icon",
      iconComponent: mapLabelToComponent("Schedule"),
      label: "Schedule",
      mobileIcon: "member_schedule.svg",
      route: this.props.url + "/schedule",
    },
    {
      activeIconComponent: mapLabelToComponent("Messages", true),
      icon: "NavMenu__messages-icon",
      iconComponent: mapLabelToComponent("Messages"),
      label: "Messages",
      mobileIcon: "member_messages.svg",
      route: this.props.url + "/messages",
    },
  ];

  ACTION_SECTION_OPTIONS = [];

  navbarConfiguration = {
    customRenderSection: [
      {
        componentProps: {
          config: {
            options: this.ACTION_SECTION_OPTIONS,
          },
        },
        isComponent: true,
        render: ActionSection,
      },
    ],
    routeSection: {
      options: this.ROUTE_SECTION_OPTIONS,
    },
    selfSection: {},
  };

  state = {
    acknowledgements: null,
    incompleteAcknowledgements: null,
    logInTasks: [],
    scheduleList: null,
    showMobileAppBanner: false,
    showPaywall: false,
    showSideDrawyer: false,
    skippedLoginTasks: new Set(), // Track skipped tasks by task_type during session
  };

  async componentDidMount() {
    const { fetchPatientPaywall, getAssessmentStatus, getPatientProfile } = this.props;

    await Promise.allSettled([getPatientProfile(), fetchPatientPaywall()]);

    if (
      this.props.profile &&
      (Object.hasOwn(this.props.profile, "acknowledgements") || this.props.profile.tasks.length)
    ) {
      this.processProfileAcknowledgements(this.props.profile);
    }

    getAssessmentStatus();
    this.checkForMobileApp();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !prevProps.profile &&
      this.props.profile &&
      (Object.hasOwn(this.props.profile, "acknowledgements") || this.props.profile.tasks.length)
    ) {
      this.processProfileAcknowledgements(this.props.profile);
    }

    if (!prevProps.patientCardInfo.showPaywall && this.props.patientCardInfo.showPaywall) {
      this.setState({
        ...this.state,
        showPaywall: true,
      });
    }

    if (this.isAcknowledgementNeeded(prevProps, prevState)) {
      this.props.history.push(
        `/app/acknowledgments/${Object.keys(this.state.incompleteAcknowledgements)[0]}`,
      );
    } else if (this.isLogInTasksNeeded(prevProps, prevState)) {
      this.props.history.push(`/app/log-in-tasks/${this.state.logInTasks[0]?.task_type}`);
    } else if (this.isPaywallNeeded()) {
      this.props.history.push("/app/payment-check");
    }
  }

  isPaywallNeeded = () => {
    return (
      !this.state.incompleteAcknowledgements &&
      !this.state.logInTasks.length &&
      this.state.showPaywall &&
      window.location.pathname !== "/app/payment-check"
    );
  };

  isLogInTasksNeeded = (_prevProps, prevState) => {
    if (!prevState.logInTasks.length && this.state.logInTasks.length) {
      return true;
    }

    if (
      prevState.logInTasks.length &&
      this.state.logInTasks.length &&
      !_.isEqual(prevState.logInTasks.length, this.state.logInTasks.length)
    ) {
      return true;
    }

    // Account for finishing acknowledgments
    if (
      prevState.incompleteAcknowledgements !== undefined &&
      this.state.incompleteAcknowledgements === undefined &&
      this.state.logInTasks.length
    ) {
      return true;
    }
  };

  processProfileAcknowledgements(profile) {
    const acknowledgements = profile.acknowledgements;
    const newState = {};
    const incompleteAcknowledgements = this.getIncompleteAcknowledgements(acknowledgements);
    const incompleteLogInTasks = this.getIncompleteLogInTasks(profile);

    if (!_.isNull(incompleteAcknowledgements)) {
      newState["incompleteAcknowledgements"] = incompleteAcknowledgements;
      newState["acknowledgements"] = acknowledgements;
    }

    if (incompleteLogInTasks.length) {
      newState["logInTasks"] = incompleteLogInTasks;
    }

    if (!_.isEmpty(newState)) {
      this.setState(newState);
    }
  }

  isAcknowledgementNeeded = (_prevProps, prevState) => {
    if (!prevState.incompleteAcknowledgements && this.state.incompleteAcknowledgements) {
      return true;
    }

    if (
      prevState.incompleteAcknowledgements &&
      this.state.incompleteAcknowledgements &&
      !_.isEqual(prevState.incompleteAcknowledgements, this.state.incompleteAcknowledgements)
    ) {
      return true;
    }
  };

  getIncompleteAcknowledgements(acknowledgements) {
    if (_.isNull(acknowledgements) && _.isEmpty(acknowledgements)) {
      return;
    }

    const incompleteAcknowledgements = Object.entries(acknowledgements)
      .filter(([, ackVal]) => ackVal === null)
      .reduce((obj, [key, value]) => {
        obj[key] = value;

        return obj;
      }, {});

    if (_.isEmpty(incompleteAcknowledgements)) {
      return;
    }

    return incompleteAcknowledgements;
  }

  onAcknowledgementComplete = (currentAcknowledgement, acknowledgementData) => {
    let incompleteAcknowledgements = {
      ...this.state.incompleteAcknowledgements,
    };

    delete incompleteAcknowledgements[currentAcknowledgement];
    incompleteAcknowledgements = this.getIncompleteAcknowledgements(incompleteAcknowledgements);

    const acknowledgements = { ...this.state.acknowledgements };

    acknowledgements[currentAcknowledgement] = acknowledgementData;

    store.dispatch(
      profileFetchSuccess({
        ...this.props.profile,
        acknowledgements,
      }),
    );

    this.setState({ acknowledgements, incompleteAcknowledgements });
    this.canPushToHome();
  };

  onTaskCompleted = (task) => {
    this.removeLogInTask(task, "completed");
  };

  onTaskSkipped = (task) => {
    // Add task_type to skipped set to prevent re-showing during this session
    this.setState(
      (prevState) => ({
        skippedLoginTasks: new Set(prevState.skippedLoginTasks).add(task.task_type),
      }),
      () => {
        // Remove from current task list to allow navigation, but keep in profile.tasks
        this.removeLogInTask(task, "skipped");
      },
    );
  };

  canPushToHome = () => {
    if (
      !this.state.incompleteAcknowledgements &&
      !this.state.logInTasks.length &&
      !this.state.showPaywall
    ) {
      window.localStorage.removeItem("redirectAfterAuth");
      this.props.history.push("/app/home");
    }
  };

  removeLogInTask = (task, reason = "completed") => {
    const tasks = this.state.logInTasks;
    const { policy_type, target_patient_id, task_type } = task;
    const updatedTasks = tasks.filter(
      (item) =>
        !(
          item.policy_type === policy_type &&
          item.target_patient_id === target_patient_id &&
          item.task_type === task_type
        ),
    );

    /** Only filter out tasks from profile store when they're completed, not skipped
     * Skipped tasks should remain in profile.tasks so TasksWidget can display them
     */
    if (!updatedTasks.length && reason === "completed") {
      store.dispatch(
        profileFetchSuccess({
          ...this.props.profile,
          tasks: this.filterOutLogInTasks(this.props.profile.tasks),
        }),
      );
    }

    this.setState({ logInTasks: updatedTasks }, () => {
      this.canPushToHome();
    });
  };

  filterOutLogInTasks = (tasks) => {
    return tasks.filter((item) => !item.display_log_in);
  };

  toggleSideDrawyer = () => {
    this.setState((prevState) => {
      return {
        showSideDrawyer: !prevState.showSideDrawyer,
      };
    });
  };

  onPaywallCompleted = () => {
    this.setState({
      ...this.state,
      showPaywall: false,
    });

    this.props.history.push("/app");
  };

  checkForMobileApp = async () => {
    try {
      if (this.deviceInfo.isMobile && !sessionStorage.getItem("mobileAppBannerClosed")) {
        if (this.deviceInfo.os.toLowerCase() === "ios") {
          this.setState({
            ...this.state,
            showMobileAppBanner: true,
          });
        } else if (this.deviceInfo.os.toLowerCase() === "android") {
          const relatedApps = await navigator.getInstalledRelatedApps();

          if (relatedApps) {
            const psApp = relatedApps.find((app) => app.id === "com.aptihealth.aptihealth");

            if (!psApp) {
              this.setState({
                ...this.state,
                showMobileAppBanner: true,
              });
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  handleCloseMobileBanner = () => {
    this.setState({
      ...this.state,
      showMobileAppBanner: false,
    });
    sessionStorage.setItem("mobileAppBannerClosed", true);
  };

  getIncompleteLogInTasks = (profile) => {
    const logInTasks =
      profile?.tasks?.filter(
        (task) => task.display_log_in && !this.state.skippedLoginTasks.has(task.task_type),
      ) || [];

    return Array.from(logInTasks).sort((a, b) => {
      if (a.rank === null) {
        return 1;
      }
      if (b.rank === null) {
        return -1;
      }

      return b.rank - a.rank;
    });
  };

  renderAcknowledgementLayout = () => {
    // binds current login task with utility methods to store in the context
    const currentTask = this.state.logInTasks[0] || null;

    const logInTaskContextValue = {
      onTaskCompleted: () => {
        if (currentTask) {
          this.onTaskCompleted(currentTask);
        }
      },
      onTaskSkipped: () => {
        if (currentTask) {
          this.onTaskSkipped(currentTask);
        }
      },
      task: currentTask,
    };

    return (
      <LogInTaskProvider value={logInTaskContextValue}>
        <BrandedHeader />

        <Main2>
          <Switch>
            <Route
              path="/app/acknowledgments/:policy_name"
              render={(props) => (
                <PolicyAcknowledgement
                  {...props}
                  acknowledgements={this.state.acknowledgements}
                  incompleteAcknowledgements={this.state.incompleteAcknowledgements}
                  onAcknowledgementComplete={this.onAcknowledgementComplete}
                />
              )}
            />

            <Route
              path="/app/log-in-tasks/:task_name"
              render={(props) => {
                // Redirect if no tasks available to prevent undefined errors
                if (!this.state.logInTasks.length) {
                  this.props.history.replace("/app/home");

                  return null;
                }

                return <LogInTasksRouter {...props} />;
              }}
            />

            <Route
              path="/app/payment-check"
              render={(props) => (
                <PaymentPaywall {...props} onPaywallCompleted={this.onPaywallCompleted} />
              )}
            />
          </Switch>
        </Main2>
      </LogInTaskProvider>
    );
  };

  renderBaseLayout = () => {
    return (
      <>
        {this.state.showMobileAppBanner && this.deviceInfo && (
          <AppBanner deviceInfo={this.deviceInfo} onClose={this.handleCloseMobileBanner} />
        )}

        <Toolbar
          baseRoute={baseRoute}
          navbarConfiguration={this.navbarConfiguration}
          sidebarActive={this.state.showSideDrawyer}
          toggleHandler={this.toggleSideDrawyer}
        />

        <Main2>
          <Switch>
            {/* ===== HOME DASHBOARD ===== */}
            <Route path="/app/home" render={(props) => <PatientHome {...props} />} />
            {/* ===== USER PROFILE & ACCOUNT MANAGEMENT ===== */}
            <Route component={PatientProfile} path="/app/profile" />
            <Route
              path="/app/change-password"
              render={(props) => <ChangePassword {...props} baseRoute="/app" />}
            />
            <Route component={PatientNotifications} path="/app/patient-notifications" />
            <Route component={PatientInsurance} path="/app/patient-insurance" /> {/* migrate */}
            <Route component={PaymentsPage} path="/app/patient-payment" />
            <Route component={MobileAddPayment} path="/app/mobile-add-payment" />
            {/* ===== CARE & HEALTH MANAGEMENT ===== */}
            <Route component={Assessment} path="/app/assessment" />
            <Route
              path="/app/outcome-assessment"
              render={(props) => <Assessment {...props} iteration="post_90_day" />}
            />
            <Route component={PHQ9} path="/app/phq-9" />
            <Route component={GAD7} path="/app/gad-7" />
            <Route component={SNSEAssessment} path="/app/sns-e" />
            <Route component={Survey} path="/app/satisfaction-survey" />
            <Route component={ACPSatisfactionSurvey} path="/app/acp-satisfaction" />
            <Route component={ScheduleRouter} path="/app/schedule" />
            <Route component={TeensAndKidsCareProgram} path="/app/teens-and-kids/:step" />
            {/* ===== COMMUNICATION ===== */}
            <Route path="/app/messages" render={(props) => <MessagesView {...props} />} />
            <Route component={PostSessionSurveyWrapper} path="/app/video-call/ratings" />
            <Route path="/app/video-call" render={() => <VideoClient {...this.props} />} />
            {/* ===== SETTINGS & PREFERENCES ===== */}
            <Route
              path="/app/settings"
              render={(props) => <PatientSettings {...props} baseRoute="/app" />}
            />
            {/* ===== SUPPORT & TECHNICAL ===== */}
            <Route path="/app/tutorial" render={() => <TutorialScreens baseRoute="/app" />} />
            <Route component={SystemCheck} path="/app/system-check" />
            <Route component={AVCheck} path="/app/av-check" />
            {/* ===== LEGAL & COMPLIANCE ===== */}
            <Route
              path="/app/consents"
              render={(props) => {
                if (this.props.isACPEligible && this.props.isPatientAdult)
                  return <PatientConsents {...props} />;

                return <Redirect to="/app" />;
              }}
            />
            <Route
              path="/app/privacy-policy"
              render={(props) => <PrivacyPolicy {...props} baseRoute="/app" />}
            />
            <Route
              path="/app/acknowledgments/:policy_name"
              render={(props) => (
                <PolicyAcknowledgement
                  {...props}
                  acknowledgements={this.state.acknowledgements}
                  onAcknowledgementComplete={this.onAcknowledgementComplete}
                />
              )}
            />
            {/* ===== FALLBACK ===== */}
            <Redirect from="/app" to="/app/home" />
          </Switch>
        </Main2>

        <Footer />
      </>
    );
  };

  render() {
    if (
      this.props.profile &&
      this.props.patientCardInfo.showPaywall !== undefined &&
      !this.state.incompleteAcknowledgements &&
      !this.state.showPaywall &&
      !this.state.logInTasks.length
    ) {
      return <>{this.renderBaseLayout()}</>;
    }

    if (
      this.props.profile &&
      this.props.patientCardInfo.showPaywall !== undefined &&
      (this.state.incompleteAcknowledgements ||
        this.state.showPaywall ||
        this.state.logInTasks.length)
    ) {
      return <>{this.renderAcknowledgementLayout()}</>;
    }

    return <Spinner />;
  }
}

const mapStateToProps = (state) => {
  return {
    assessmentStatus: state.patient.assessmentStatus,
    configs: state.configs,
    flags: state.flags,
    isACPEligible: selectIsACPEligible(state),
    isPatientAdult: isAdult(state?.auth?.profile?.dob),
    patientCardInfo: state.patientCardInfo,
    profile: state.auth.profile,
    scheduleList: state.patient.scheduleList,
  };
};

export default connect(mapStateToProps, {
  fetchPatientPaywall,
  getAssessmentStatus,
  getPatientProfile,
  updateScheduleList,
})(withRouter(PatientRouter));
